
import { Component, Emit, Prop } from 'vue-property-decorator';
import VFormBuilder from '@/components/shared/form/VFormBuilder.vue';
import { InputType } from '@/enums/InputType';
import CustomerGroup from '@/mixins/CustomerGroup';
import { mixins } from 'vue-class-component';
import { Venue } from '@/interfaces/models/Venue';
import { CouponType } from '@/enums/CouponType';
import StackedForm from '@/mixins/StackedForm';
import Countries from '@/mixins/Countries';
import { Article } from '@/interfaces/models/Article';
import { SelectItem } from '@/interfaces/components/SelectItem';
import { FormItem } from '@/interfaces/components/FormItem';
import { venueFilter } from '@/util/helper';
import { Coupon } from '@/interfaces/models/Coupon';

@Component({
  components: { VFormBuilder },
})
export default class SimpleGeneralForm extends mixins(CustomerGroup, StackedForm, Countries) {
  @Prop({ type: Array, default: () => [] }) public venues!: Venue[];
  @Prop({ type: Array, default: () => [] }) public articles!: Article[];
  @Prop({ type: Boolean, required: true }) public editing!: boolean;
  @Prop({ type: Boolean, default: false }) public multiple!: boolean;

  get types() {
    return [
      { text: this.$t('coupon.types.absolute'), value: CouponType.Absolute },
      { text: this.$t('coupon.types.relative'), value: CouponType.Relative },
      { text: this.$t('coupon.types.deliveryFee'), value: CouponType.Delivery_Fee },
      { text: this.$t('coupon.types.giftCard'), value: CouponType.Gift_Card },
      { text: this.$t('coupon.types.bogo'), value: CouponType.Bogo },
      { text: this.$t('coupon.types.reward'), value: CouponType.Reward },
      { text: this.$t('coupon.types.freeArticle'), value: CouponType.Free_Article },
      { text: this.$t('coupon.types.void'), value: CouponType.Void },
      { text: this.$t('coupon.types.absoluteArticle'), value: CouponType.Absolute_Article },
      { text: this.$t('coupon.types.relativeArticle'), value: CouponType.Relative_Article },
    ];
  }

  get items() {
    const items: FormItem[] = [
      { name: 'name', type: InputType.Text, label: 'coupon.form.name', rules: 'required' },
      { name: 'code', type: InputType.Text, label: 'coupon.form.code', rules: 'required' },
      { name: 'range', type: InputType.DateRange, rules: 'required' },
      {
        name: 'countries',
        type: InputType.Select,
        multiple: true,
        label: 'coupon.form.countries',
        items: this.selectableCountries,
        change: this.onCountriesChange,
      },
      {
        name: 'venues',
        type: InputType.Autocomplete,
        label: 'coupon.form.venues',
        multiple: true,
        itemValue: '_id',
        itemText: 'name',
        items: this.venues,
        filter: venueFilter,
      },
      {
        name: 'type',
        type: InputType.Select,
        label: 'coupon.form.type',
        items: this.types,
        disabled: this.typeDisabledCheck,
        rules: 'required',
      },
      {
        name: 'value',
        type: InputType.Text,
        label: 'coupon.form.discount',
        default: 0,
        visible: this.absoluteValueVisibleCallbackDefault,
        rules: 'required',
      },
      {
        name: 'value',
        type: InputType.Text,
        label: 'coupon.form.percent',
        default: 0,
        visible: this.relativeValueVisibleCallbackDefault,
        rules: 'required',
      },
      {
        name: 'article',
        type: InputType.Autocomplete,
        label: 'coupon.form.article',
        visible: this.discountArticleVisibleCallbackDefault,
        rules: 'required',
        items: this.articleItems,
      },
      {
        name: 'value',
        type: InputType.Autocomplete,
        label: 'coupon.form.bogoArticles',
        visible: this.bogoValueVisibleCallbackDefault,
        rules: 'required',
        multiple: true,
        items: this.articleItems,
      },
      {
        name: 'value',
        type: InputType.Autocomplete,
        label: 'coupon.form.freeArticle',
        multiple: true,
        visible: this.freeArticleValueVisibleCallbackDefault,
        rules: 'required',
        items: this.articleItems,
      },
      {
        name: 'giftCards',
        type: InputType.File,
        label: 'coupon.form.uploadGiftCards',
        visible: this.uploadGiftCardsVisibleCallbackDefault,
        rules: 'required',
        default: null,
        accept: '.csv',
        items: this.articleItems,
      },
      { name: 'mov', type: InputType.Text, label: 'coupon.form.mov', default: '0', rules: 'required' },
      { name: 'amount', type: InputType.Text, label: 'coupon.form.amount', default: '0', rules: 'required' },
      {
        name: 'isMultipleApplicable',
        type: InputType.Checkbox,
        label: 'coupon.form.isMultipleApplicable',
        default: false,
      },
      { name: 'active', type: InputType.Checkbox, label: 'coupon.form.active', default: true },
    ];

    if (this.multiple) {
      return [
        ...items.filter((i: FormItem) => i.name !== 'code'),
        {
          name: 'generateAmount',
          type: InputType.Text,
          default: 10,
          label: 'coupon.form.generateAmount',
          rules: 'required|integer',
        },
      ];
    }

    return items;
  }

  @Emit('countries')
  public onCountriesChange(val: string[], form: { countries: string[] }) {
    return { countries: form.countries, customerGroup: null };
  }

  get selectableCountries() {
    const countries: string[] = this.venues.map((v: Venue) => (v.country ? v.country.toLowerCase() : v.country));

    return this.countries.filter((i: SelectItem) => countries.includes(i.value as string));
  }

  get articleItems() {
    return this.articles.map((a: Article) => {
      return {
        value: a._id,
        text: this.$options.filters!.localized(a.name),
      };
    });
  }

  public uploadGiftCardsVisibleCallbackDefault(form: { type?: CouponType }) {
    return !!(form.type && [CouponType.Gift_Card].indexOf(form.type!) > -1);
  }

  public absoluteValueVisibleCallbackDefault(form: { type?: CouponType }) {
    return !!(form.type && [CouponType.Absolute, CouponType.Absolute_Article].indexOf(form.type!) > -1);
  }

  public relativeValueVisibleCallbackDefault(form: { type?: CouponType }) {
    return !!(form.type && [CouponType.Relative, CouponType.Relative_Article].indexOf(form.type!) > -1);
  }

  public discountArticleVisibleCallbackDefault(form: { type?: CouponType }) {
    return !!(form.type && [CouponType.Relative_Article, CouponType.Absolute_Article].indexOf(form.type!) > -1);
  }

  public bogoValueVisibleCallbackDefault(form: { type?: CouponType }) {
    return !!(form.type && [CouponType.Bogo].indexOf(form.type!) > -1);
  }

  public freeArticleValueVisibleCallbackDefault(form: { type?: CouponType }) {
    return !!(form.type && [CouponType.Free_Article].indexOf(form.type!) > -1);
  }

  public plusOneFreeValueVisibleCallbackDefault(form: { type?: CouponType }) {
    return !!(form.type && [CouponType.Plus_One_Free].indexOf(form.type!) > -1);
  }

  public typeDisabledCheck() {
    return this.editing;
  }

  public getData() {
    const data: Partial<Coupon> = { ...this.$refs.form.form, ...this.$refs.form.form.range };
    if ([CouponType.Absolute_Article, CouponType.Relative_Article].includes(data.type!)) {
      data.value = {
        discount: data.value,
        article: data.article,
      };
    }
    return data;
  }
}
